// ChatAgentFAB.js - Futuristic Floating Action Button
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSpring, animated } from '@react-spring/web';
import { toggleChatAgent } from '../../containers/chat-agent/actions';
import { SUPPORTED_SCREENS } from '../../containers/chat-agent/constants';
import hasAIPermission from '../../utils/auth/hasAIPermission';
import styles from './ChatAgentFAB.scss';

const SparkleIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none"
  >
    <defs>
      <linearGradient id="sparkleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3d7ffc" />
        <stop offset="50%" stopColor="#20bcd5" />
        <stop offset="100%" stopColor="#3d7ffc" />
      </linearGradient>
    </defs>
    <path 
      d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" 
      fill="url(#sparkleGradient)"
    />
    <circle cx="12" cy="10" r="1.5" fill="white" opacity="0.9" />
    <path 
      d="M7 4L7.5 6L9.5 6.5L7.5 7L7 9L6.5 7L4.5 6.5L6.5 6L7 4Z" 
      fill="url(#sparkleGradient)"
      opacity="0.7"
    />
    <path 
      d="M17 15L17.5 17L19.5 17.5L17.5 18L17 20L16.5 18L14.5 17.5L16.5 17L17 15Z" 
      fill="url(#sparkleGradient)"
      opacity="0.7"
    />
  </svg>
);

function ChatAgentFAB({ isOpen, currentScreen, toggleChatAgent }) {
  // Don't render if no AI permission
  if (!hasAIPermission()) {
    return null;
  }
  
  // Only show on supported screens
  if (!currentScreen || !SUPPORTED_SCREENS.includes(currentScreen)) {
    return null;
  }
  
  // Don't show FAB when chat is open (chat has its own close button)
  if (isOpen) {
    return null;
  }
  
  // Spring animation for hover effect
  const [hovered, setHovered] = React.useState(false);
  const springProps = useSpring({
    scale: hovered ? 1.05 : 1,
    y: hovered ? -4 : 0,
    config: { tension: 300, friction: 20 }
  });
  
  return (
    <animated.button
      className={styles.fab}
      onClick={toggleChatAgent}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        transform: springProps.y.to(y => `translateY(${y}px) scale(${springProps.scale.get()})`)
      }}
      title="Open AI Assistant"
      aria-label="Open AI Assistant"
    >
      <div className={styles.fabGlow} />
      <div className={styles.fabParticles} />
      <div className={styles.fabContent}>
        <SparkleIcon />
        <span className={styles.label}>AI Assistant</span>
      </div>
    </animated.button>
  );
}

const mapStateToProps = (state) => ({
  isOpen: state.chatAgent?.isOpen || false,
  currentScreen: state.chatAgent?.currentScreen || null,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ toggleChatAgent }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChatAgentFAB);

